import React, { useState, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import BrowserOnly from 'components/consumer/BrowserOnly';
import PDPView3DButton from 'components/consumer/PDP/PDPView3DButton';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import PDPFullScreenGallery from 'components/consumer/PDP/PDPFullScreenGallery';
import PDPMobileGalleryZoomIcon from 'components/consumer/PDP/PDPMobileGalleryZoomIcon';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import CSSScrollSnapCarousel from 'components/consumer/CSSScrollSnapCarousel';
import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPShownIn from '../PDPShownIn';

// Return items that have a unique id or videoId field
const getUniqueCarouselItems = items => {
  const uniqueItems = [];
  const seenIds = new Set();

  items.forEach(item => {
    const id = item.id || item.videoId;
    if (id && !seenIds.has(id)) {
      seenIds.add(id);
      uniqueItems.push(item);
    }
  });

  return uniqueItems;
};

const DEFAULT_PHOTOS = [];

const PDPMobileCarousel = ({
  displayShownIn = false,
  enableCrops = true,
  headerLeft,
  isConfigFlipped = false,
  isEditOrder = false,
  isHero = false,
  maxPhotos = 16,
  onAfterSlide,
  photos = DEFAULT_PHOTOS,
  productName,
  rightButtons,
  showARIcon = false,
  showDecideLaterOverlay = false,
  showFavoriteButton = false,
  videoId,
  zoomOnClick = true,
}) => {
  const validPhotos = useRemoveBrokenImageUrls(photos);

  const isConfigurationGallery =
    validPhotos?.[0]?.type === 'configuration' ||
    validPhotos?.[0]?.type === 'cylindo';

  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});

  const hasMoreThanMaxPhotos = validPhotos.length > maxPhotos;

  const carouselPhotos = useMemo(() => {
    const trimmedPhotos = hasMoreThanMaxPhotos
      ? validPhotos.slice(0, maxPhotos)
      : validPhotos;

    const photosAndVideo =
      videoId && trimmedPhotos.length
        ? [trimmedPhotos[0], { videoId }, ...trimmedPhotos.slice(1)]
        : trimmedPhotos;

    return photosAndVideo;
  }, [hasMoreThanMaxPhotos, validPhotos, maxPhotos, videoId]);

  const standardRatio = isConfigurationGallery ? 2 / 3 : 1 / 2;

  const scrollDownToCustomizer = useCallback(() => {
    if (!displayShownIn) {
      return;
    }
    scrollIntoViewUtil('[data-xc="PDPMobileConfigurationPhotoGalleryUI"]', 0);
  }, [displayShownIn]);

  const handleSlideChange = useCallback(
    slideIndex => {
      setSelectedCarouselIndex(slideIndex);
      onAfterSlide?.(slideIndex);
    },
    [onAfterSlide]
  );

  const uniqueCarouselPhotos = useMemo(
    () => getUniqueCarouselItems(carouselPhotos),
    [carouselPhotos]
  );

  // Convert uniqueCarouselPhotos to slides with explicit keys
  const slides = useMemo(
    () =>
      uniqueCarouselPhotos
        .map(
          (
            {
              src,
              id,
              type,
              cropData,
              // eslint-disable-next-line no-shadow
              videoId,
              optionId,
              upholsteryName,
              valueId,
            },
            index
          ) => {
            const slideKey = videoId
              ? `video-${videoId}`
              : `${id || src || index}`;

            return {
              key: slideKey,
              slide: (
                <RatioContainer
                  className={classNames({
                    'pt-8 px-6 pb-11': isConfigurationGallery && !enableCrops,
                    'scale-x-[-1]': isConfigurationGallery && isConfigFlipped,
                    'flex justify-center': brokenImages[src],
                  })}
                  ratio={enableCrops ? 1 : standardRatio}
                >
                  {index === 1 && videoId ? (
                    <PDPProductIntroVideo videoId={videoId} />
                  ) : (
                    <>
                      {displayShownIn ? (
                        <PDPShownIn
                          isEditOrder={isEditOrder}
                          optionId={optionId}
                          upholsteryName={upholsteryName}
                          valueId={valueId}
                          layout="roundSwatchLabel"
                          onSelection={scrollDownToCustomizer}
                          productName={productName}
                          showInactiveUpholstery
                        />
                      ) : null}
                      <JBProductImage
                        className="h-full object-contain w-full"
                        {...getMainImageImgixPropsForPhoto(
                          { src, type, cropData },
                          enableCrops
                        )}
                        lazy={index !== 0}
                        sizes="100vw"
                        onClick={
                          zoomOnClick
                            ? showDecideLaterOverlay
                              ? () => {}
                              : () => {
                                  setSelectedCarouselIndex(index);
                                  setIsFullScreenGalleryOpen(true);
                                }
                            : undefined
                        }
                        forPage="pdp"
                        onLoad={isFail => {
                          if (index === 0 && isHero) {
                            markLuxPageNavigationEnd();
                          }

                          if (!isFail) {
                            return;
                          }
                          setBrokenImages({ ...brokenImages, [src]: true });
                        }}
                      />
                    </>
                  )}

                  {hasMoreThanMaxPhotos &&
                    index === uniqueCarouselPhotos.length - 1 && (
                      <div className="items-center bottom-0 flex justify-center left-0 pointer-events-none absolute right-0 top-0">
                        <div className="text-lg font-bold bg-brand-lightest text-brand-dark px-4 py-3">
                          Explore{' '}
                          {validPhotos.length - uniqueCarouselPhotos.length}+
                          more photos
                        </div>
                      </div>
                    )}
                </RatioContainer>
              ),
            };
          }
        )
        .filter(Boolean),
    [
      brokenImages,
      displayShownIn,
      enableCrops,
      hasMoreThanMaxPhotos,
      isConfigFlipped,
      isConfigurationGallery,
      isEditOrder,
      isHero,
      productName,
      scrollDownToCustomizer,
      setBrokenImages,
      setIsFullScreenGalleryOpen,
      setSelectedCarouselIndex,
      showDecideLaterOverlay,
      standardRatio,
      uniqueCarouselPhotos,
      validPhotos,
      zoomOnClick,
    ]
  );

  return (
    <>
      <div className="relative">
        <CSSScrollSnapCarousel
          height="medium"
          onSlideChange={handleSlideChange}
          slides={slides}
        />

        <div
          className={classNames('absolute right-4 top-4 z-[1]', {
            'justify-between left-4': !displayShownIn,
            'justify-end': displayShownIn,
            flex: !showDecideLaterOverlay,
            '!hidden': showDecideLaterOverlay,
          })}
        >
          {!displayShownIn ? (
            <div className="pointer-events-none">
              {!!headerLeft && headerLeft}
            </div>
          ) : null}

          <div className="flex [&_>_*_+_*]:ml-2.5">
            {showARIcon && <PDPView3DButton />}
            {!!zoomOnClick && !(selectedCarouselIndex === 1 && videoId) && (
              <PDPMobileGalleryZoomIcon
                inverted={isConfigurationGallery}
                onClick={() => {
                  setIsFullScreenGalleryOpen(true);
                }}
              />
            )}

            <div className="flex flex-col gap-2">
              {showFavoriteButton && (
                <PDPFavoriteButton size="sm" className="ml-0" />
              )}

              {!!rightButtons && rightButtons}
            </div>
          </div>
        </div>
      </div>

      <BrowserOnly>
        <PDPFullScreenGallery
          isEditOrder={isEditOrder}
          isOpen={isFullScreenGalleryOpen}
          photos={validPhotos}
          initialSelectedPhotoIndex={selectedCarouselIndex}
          onClose={() => {
            setIsFullScreenGalleryOpen(false);
          }}
          productName={productName}
          displayShownIn={displayShownIn}
        />
      </BrowserOnly>
    </>
  );
};

PDPMobileCarousel.propTypes = {
  displayShownIn: PropTypes.bool,
  enableCrops: PropTypes.bool,
  headerLeft: PropTypes.node,
  isConfigFlipped: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isHero: PropTypes.bool,
  maxPhotos: PropTypes.number,
  onAfterSlide: PropTypes.func,
  onDimensionsIconClick: PropTypes.func,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  productName: PropTypes.string,
  rightButtons: PropTypes.node,
  showARIcon: PropTypes.bool,
  showDecideLaterOverlay: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  videoId: PropTypes.string,
  zoomOnClick: PropTypes.bool,
};

export default PDPMobileCarousel;
